import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type DisplayModel, type Role } from "../api";
import { useAuth } from "../auth";
import { AllCheck, BulkBar, RowCheck, rowKey, useSelection } from "../components/Bulk";
import { errorText, useLoad } from "../hooks";

export function AdminPage() {
  const { user } = useAuth();
  const sites = useLoad(() => api.sites(), []);
  const users = useLoad(() => api.users(), []);
  const patients = useLoad(() => api.patients(), []);
  const models = useLoad(() => api.displayModels(), []);
  const settings = useLoad(() => api.adminSettings(), []);
  const [siteName, setSiteName] = useState("");
  const [nu, setNu] = useState({ username: "", role: "operator" as Role, site_id: "", patient_id: "", email: "", display_name: "", notify: false });
  const [temp, setTemp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const siteSel = useSelection();
  const modelSel = useSelection();
  const userSel = useSelection();
  if (user?.role !== "admin") return <p>Admins only.</p>;
  const siteKeys = (sites.data ?? []).map((s) => rowKey("sites", s.id));
  const modelKeys = (models.data ?? []).map((m) => rowKey("display_models", m.id));
  const userKeys = (users.data ?? []).filter((u) => u.id !== user.id).map((u) => rowKey("users", u.id));
  const patientCount = (siteId: number) => (patients.data ?? []).filter((p) => p.site_id === siteId).length;

  const wrap = async (f: () => Promise<void>) => {
    setError(null);
    try { await f(); } catch (e) { setError(errorText(e)); }
  };

  return (
    <>
      <h1>Admin</h1>
      <div className="card">
        <strong>Deleting:</strong> tick rows in any list (here, Patients, Programs, Stations, a patient’s history) and
        use <em>Delete selected</em>. <Link to="/admin/data">Clean up data</Link> finds sessions across all patients
        by date, program, mode, status or debug, to delete many at once.
      </div>
      {temp && <div className="card"><strong>Temporary password:</strong> <code>{temp}</code><div className="small muted">Shown once. Give it to the user in person; they must change it at first sign-in.</div></div>}
      {error && <div className="error">{error}</div>}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Security</h2>
        <label style={{ flexDirection: "row", gap: "0.4rem" }}>
          <input type="checkbox" checked={!!settings.data?.require_2fa_staff}
            onChange={(e) => void wrap(async () => { await api.setAdminSettings({ require_2fa_staff: e.target.checked }); settings.reload(); })} />
          Require two-factor sign-in for staff (admins, researchers, operators)
        </label>
        <p className="small muted">Turn it on for your own account first (click your name, top right). Patients are not asked.</p>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Sites</h2>
        <BulkBar sel={siteSel} shown={siteKeys} what="sites" onDeleted={() => { sites.reload(); users.reload(); patients.reload(); }}
          presets={[{ label: "No patients", keys: (sites.data ?? []).filter((s) => !patientCount(s.id)).map((s) => rowKey("sites", s.id)) }]} />
        <table>
          <thead><tr><th className="pick-col"><AllCheck sel={siteSel} keys={siteKeys} label="Select all sites" /></th>
            <th>Site</th><th>Patients</th><th>Accounts</th></tr></thead>
          <tbody>
            {(sites.data ?? []).map((s) => (
              <tr key={s.id} className={siteSel.has(rowKey("sites", s.id)) ? "picked" : ""}>
                <td className="pick-col"><RowCheck sel={siteSel} k={rowKey("sites", s.id)} order={siteKeys} label={`Select site ${s.name}`} /></td>
                <td>{s.name} <span className="muted small">#{s.id}</span></td>
                <td>{patientCount(s.id)}</td>
                <td>{(users.data ?? []).filter((u) => u.site_id === s.id).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ marginTop: "0.8rem" }}>
          <label>New site<input value={siteName} onChange={(e) => setSiteName(e.target.value)} /></label>
          <button disabled={!siteName} onClick={() => void wrap(async () => { await api.createSite(siteName); setSiteName(""); sites.reload(); })}>Add site</button>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Approved monitors for home testing</h2>
        <p className="small muted">Add one from Stations: calibrate a monitor in the lab, then "Approve for home use".</p>
        <BulkBar sel={modelSel} shown={modelKeys} what="approved monitors" onDeleted={models.reload}
          presets={[{ label: "Turned off", keys: (models.data ?? []).filter((m) => !m.active).map((m) => rowKey("display_models", m.id)) }]} />
        <table>
          <thead><tr><th className="pick-col"><AllCheck sel={modelSel} keys={modelKeys} label="Select all approved monitors" /></th><th>Model</th><th>Screen name contains</th><th>Resolution</th><th>Size</th><th>Bits</th><th>Checked with</th><th>EDID</th><th>Home meter</th><th>Active</th></tr></thead>
          <tbody>
            {(models.data ?? []).map((m) => (
              <tr key={m.id} className={modelSel.has(rowKey("display_models", m.id)) ? "picked" : ""}>
                <td className="pick-col"><RowCheck sel={modelSel} k={rowKey("display_models", m.id)} order={modelKeys} label={`Select approved monitor ${m.name}`} /></td>
                <td>{m.name}<div className="small muted">{m.instructions}</div></td><td><code>{m.label_pattern}</code></td><td>{m.width_px}×{m.height_px}</td>
                <td>{m.width_mm.toFixed(0)}×{m.height_mm.toFixed(0)} mm</td><td>{m.output_bits}</td>
                <td>{(m.platforms ?? []).join(", ") || "any"}</td>
                <td className="small">{m.edid_vendor ? `${m.edid_vendor} ${m.edid_product}` : "—"}</td>
                <td><MeterEdit m={m} onSaved={models.reload} onError={setError} /></td>
                <td><input type="checkbox" aria-label={`${m.name} active`} checked={m.active} onChange={(e) => void wrap(async () => { await api.setDisplayModelActive(m.id, e.target.checked); models.reload(); })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small muted">Home meter: what the patients' meter should read on the white screen, measured in the lab with the
          same kind of meter on this model at the calibrated white. Blank uses the lab white in cd/m².</p>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Users</h2>
        <BulkBar sel={userSel} shown={userKeys} what="accounts" onDeleted={users.reload} presets={[
          { label: "Turned off", keys: (users.data ?? []).filter((u) => !u.is_active).map((u) => rowKey("users", u.id)) },
          { label: "Patient logins", keys: (users.data ?? []).filter((u) => u.role === "patient").map((u) => rowKey("users", u.id)) },
        ]} />
        <table>
          <thead><tr><th className="pick-col"><AllCheck sel={userSel} keys={userKeys} label="Select all accounts" /></th><th>User</th><th>Role</th><th>Site</th><th>Notify</th><th>Active</th><th /></tr></thead>
          <tbody>
            {(users.data ?? []).map((u) => (
              <tr key={u.id} className={userSel.has(rowKey("users", u.id)) ? "picked" : ""}>
                <td className="pick-col">{u.id !== user.id
                  ? <RowCheck sel={userSel} k={rowKey("users", u.id)} order={userKeys} label={`Select account ${u.username}`} />
                  : <span className="small muted" title="your own account">you</span>}</td>
                <td>{u.username}<div className="small muted">{u.display_name} {u.email}</div></td>
                <td>{u.role}</td>
                <td>{sites.data?.find((s) => s.id === u.site_id)?.name ?? "—"}</td>
                <td><input type="checkbox" checked={u.notify} onChange={(e) => void wrap(async () => { await api.updateUser(u.id, { notify: e.target.checked }); users.reload(); })} /></td>
                <td><input type="checkbox" checked={u.is_active} disabled={u.id === user.id} onChange={(e) => void wrap(async () => { await api.updateUser(u.id, { is_active: e.target.checked }); users.reload(); })} /></td>
                <td><button className="link" onClick={() => void wrap(async () => { const r = await api.updateUser(u.id, { reset_password: true }); setTemp(r.temporary_password); })}>Reset password</button>
                  {u.totp_enabled && <> · <button className="link" onClick={() => void wrap(async () => { await api.updateUser(u.id, { reset_2fa: true }); users.reload(); })}>Reset two-factor</button></>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2>Add a user</h2>
        <div className="row">
          <label>Username<input value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} /></label>
          <label>Role
            <select aria-label="Role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value as Role })}>
              <option value="operator">operator</option>
              <option value="researcher">researcher</option>
              <option value="patient">patient (home testing)</option>
              <option value="admin">admin</option>
            </select>
          </label>
          {nu.role === "operator" && (
            <label>Site
              <select aria-label="Site" value={nu.site_id} onChange={(e) => setNu({ ...nu, site_id: e.target.value })}>
                <option value="">choose…</option>
                {(sites.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
          {nu.role === "patient" && (
            <label>Patient
              <select aria-label="Patient" value={nu.patient_id} onChange={(e) => setNu({ ...nu, patient_id: e.target.value })}>
                <option value="">choose…</option>
                {(patients.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.study_id}</option>)}
              </select>
            </label>
          )}
          <label>Name<input value={nu.display_name} onChange={(e) => setNu({ ...nu, display_name: e.target.value })} /></label>
          <label>Email<input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} /></label>
          {(nu.role === "researcher" || nu.role === "admin") && (<>
            <label style={{ flexDirection: "row", gap: "0.3rem" }}><input type="checkbox" checked={nu.notify} onChange={(e) => setNu({ ...nu, notify: e.target.checked })} />notify about reports</label>
            <span className="small muted" style={{ alignSelf: "center" }}>A notice on their home page when a report is ready or a
              change is flagged (also emailed once email is set up). It does not send a password.</span>
          </>)}
          <button disabled={!nu.username} onClick={() => void wrap(async () => {
            const r = await api.createUser({
              username: nu.username, role: nu.role, display_name: nu.display_name, email: nu.email, notify: nu.notify,
              site_id: nu.site_id ? Number(nu.site_id) : null, patient_id: nu.patient_id ? Number(nu.patient_id) : null,
            });
            setTemp(r.temporary_password);
            setNu({ ...nu, username: "", display_name: "", email: "" });
            users.reload();
          })}>Create user</button>
        </div>
      </div>
    </>
  );
}

function MeterEdit({ m, onSaved, onError }: { m: DisplayModel; onSaved: () => void; onError: (e: string) => void }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(m.meter_target === null ? "" : String(m.meter_target));
  const [unit, setUnit] = useState(m.meter_unit ?? "cd/m²");
  const [tol, setTol] = useState(String(m.meter_tolerance_pct ?? 10));
  const [instr, setInstr] = useState(m.meter_instructions ?? "");
  const shown = `${m.meter_target ?? m.white_cd_m2 ?? "85"} ${m.meter_unit ?? "cd/m²"} ±${m.meter_tolerance_pct ?? 10}%`;
  if (!open) return <span className="small">{shown} <button className="link" onClick={() => setOpen(true)}>edit</button></span>;
  return (
    <div className="small" style={{ display: "grid", gap: "0.2rem" }}>
      <input aria-label="Meter target" type="number" step="any" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="lab white" />
      <input aria-label="Meter unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
      <input aria-label="Tolerance" type="number" value={tol} onChange={(e) => setTol(e.target.value)} />
      <input aria-label="Meter instructions" value={instr} onChange={(e) => setInstr(e.target.value)} placeholder="how to hold the meter" />
      <button onClick={() => void api.updateDisplayModel(m.id, {
        meter_target: target ? Number(target) : null, meter_unit: unit || null,
        meter_tolerance_pct: tol ? Number(tol) : null, meter_instructions: instr || null,
      }).then(() => { setOpen(false); onSaved(); }, (e) => onError(String(e?.message ?? e)))}>Save</button>
    </div>
  );
}
